import { FilterActions, FilterActionTypes } from '@app/pcs/actions/filter';
import {
  CreateDossierLabelSuccess,
  FetchLabelsSuccess,
  LabelActionTypes,
  RemoveLabelSuccess,
  SaveLabelSuccess,
  SearchLabel,
  SelectLabel,
  UpdateLabelSharedType} from '@app/pcs/actions/label';
import { RecentActivityActionTypes } from '@app/pcs/actions/recent-activity';
import { QuickSearchActionTypes } from '@app/pcs/dashboard/actions/quick-search';
import { DossierSearchKey, SharedOption, UserLabel } from '@app/pcs/models';
import { isDossierNavigationAction } from '@app/pcs/util';
import { getLabelId, getLabelName, labelIdEq } from '@app/pcs/util/user-label';
import { BackButtonActionTypes } from '@dossier/actions/back-button';
import { Action } from '@ngrx/store';
import { createHandler, createReducer, hasValue, persistReducer } from '@util';
import * as R from 'ramda';

interface LocalStorageState {
  selectedLabelIds: number[];
}

export interface State {
  labels: UserLabel[];
  selectedLabelIds: number[];
  labelSearch: string;
}

export const initialState: State = {
  labels: [],
  selectedLabelIds: [],
  labelSearch: ''
};

// Selectors
type GetLabels = (s: State) => UserLabel[];
export const getLabels: GetLabels = R.prop('labels');

type GetSelectedLabelIds = (s: State) => number[];
export const getSelectedLabelIds: GetSelectedLabelIds = R.prop('selectedLabelIds');

type GetLabelSearch = (state: State) => string;
export const getLabelSearch: GetLabelSearch = R.prop('labelSearch');

// Handlers
const fetchLabelsSuccess = (state: State, {payload: labels}: FetchLabelsSuccess): State => R.mergeRight(state, {labels});

const createDossierLabelSuccess = (state: State, {payload: label}: CreateDossierLabelSuccess): State =>
  R.mergeDeepRight(state, {
    labels: R.append(label, getLabels(state))
  }) as State;

const selectLabel = (state: State, {payload: selectedLabel}: SelectLabel): State => {
  const {selectedLabelIds} = state;
  const selectedId = selectedLabel.label.id;
  const alreadySelected = R.any(R.equals(selectedId), selectedLabelIds);
  const newSelectedLabelIds = alreadySelected
    ? R.without([selectedId], selectedLabelIds)
    : R.append(selectedId, selectedLabelIds);

  return R.mergeRight(state, {
    selectedLabelIds: R.uniq(newSelectedLabelIds) as number[]
  }) as State;
};

const deselectLabels = (state: State): State => R.mergeRight(state, {
  selectedLabelIds: []
}) as State;

const searchLabel = (state: State, {payload: labelSearch}: SearchLabel): State =>
  R.mergeDeepRight(state, {labelSearch: labelSearch || ''}) as State;

const removeLabel = (state: State, {payload: labelId}: RemoveLabelSuccess): State => {
  const labelIndex = R.findIndex(labelIdEq(labelId), state.labels);
  return deselectLabels(R.evolve({
    labels: R.remove(labelIndex, 1)
  }, state));
};

type SetNewSharedTypeIfApplicable = (sharedType: SharedOption) => (userLabel: UserLabel) => UserLabel;
const setNewSharedTypeIfApplicable: SetNewSharedTypeIfApplicable = (sharedType: SharedOption) => R.evolve({
  sharedType: R.ifElse(R.equals(SharedOption.NotApplicable), R.identity, R.always(sharedType))
});

const updateSharedType = (state: State, {payload: {sharedUsers, labelId}}: UpdateLabelSharedType): State => {
  const labelIndex = R.findIndex(labelIdEq(labelId), state.labels);
  const newSharedType = hasValue(sharedUsers) ? SharedOption.Yes : SharedOption.No;
  const applyNewSharedType = setNewSharedTypeIfApplicable(newSharedType);
  return R.evolve({
    labels: R.update(labelIndex, applyNewSharedType(state.labels[labelIndex]))
  }, state);
};

const saveLabel = (state: State, {payload: userLabel}: SaveLabelSuccess): State => {
  const labelIdx = R.findIndex(labelIdEq(getLabelId(userLabel)), state.labels);
  const {label, color} = userLabel;
  const updatedUserLabel = labelIdx !== -1
    ? {...state.labels[labelIdx], label, color}
    : userLabel;
  const updateFn = labelIdx !== -1
    ? R.update(labelIdx, updatedUserLabel)
    : R.insert(labelIdx, updatedUserLabel);
  const labels = R.compose(R.sortBy(getLabelName), updateFn)(state.labels);
  return R.mergeRight(state, {labels});
};

const fetchLabelsSuccessHandler = createHandler(LabelActionTypes.FetchLabelsSuccess, fetchLabelsSuccess);
const selectLabelHandler = createHandler(LabelActionTypes.SelectLabel, selectLabel);
const createDossierLabelSuccessHandler = createHandler(LabelActionTypes.CreateDossierLabelSuccess, createDossierLabelSuccess);
const deselectHandler = createHandler([
  FilterActionTypes.SelectFilter,
  RecentActivityActionTypes.SelectRecentActivity,
  QuickSearchActionTypes.SetSearchTerm
],
deselectLabels
);
const searchDossierLabelHandler = createHandler(LabelActionTypes.SearchLabel, searchLabel);
const resetSearchHandler = createHandler(LabelActionTypes.ResetSearch, searchLabel);
const removeLabelHandler = createHandler(LabelActionTypes.RemoveLabel, removeLabel);
const saveLabelHandler = createHandler(LabelActionTypes.SaveLabelSuccess, saveLabel);
const shareLabelHandler = createHandler(LabelActionTypes.UpdateLabelSharedType, updateSharedType);

const labelsReducer = createReducer<State, FilterActions>([
  fetchLabelsSuccessHandler,
  selectLabelHandler,
  createDossierLabelSuccessHandler,
  deselectHandler,
  searchDossierLabelHandler,
  resetSearchHandler,
  removeLabelHandler,
  shareLabelHandler,
  saveLabelHandler
], initialState);

export const reducer = persistReducer<State, LocalStorageState>(
  {
    storageKey: DossierSearchKey.Labels,
    mergeStates: (state: State, {selectedLabelIds}: LocalStorageState): State =>
      R.mergeRight(state, {selectedLabelIds: selectedLabelIds}),
    mapper: R.pick(['selectedLabelIds']),
    shouldSync: (_, action: Action): boolean => isDossierNavigationAction(action),
    shouldReinitialize: (_, action: Action): boolean => action.type === BackButtonActionTypes.GoBack
  },
  labelsReducer
);
